import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/shadcn/avatar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/shadcn/tooltip';
import { Link } from '@inertiajs/react';
import { UserRound } from 'lucide-react';

export type AssignedPerson = { id: string; first_name?: string | null; last_name?: string | null; avatar?: string | null } | null | undefined;

/**
 * One person in an "Assigned" column: a bare avatar whose tooltip carries the name
 * and what they are on this record. `role` is what tells several bubbles apart —
 * without it a row of faces is unreadable.
 *
 * An unfilled seat is a dashed ring rather than a blank, so the column keeps its
 * shape and the tooltip can still say which seat is empty.
 *
 * Shared rather than per-table: projects show three seats and leads show one, but
 * they have to read as the same thing in every list and on every detail page.
 */
export function AssignedBubble({ person, role }: { person: AssignedPerson; role: string }) {
    if (!person) {
        return (
            <Tooltip>
                <TooltipTrigger asChild>
                    <span
                        aria-label={role}
                        className="border-muted-foreground/40 text-muted-foreground flex size-7 shrink-0 items-center justify-center rounded-full border border-dashed"
                    >
                        <UserRound className="size-3.5" />
                    </span>
                </TooltipTrigger>
                <TooltipContent>{role}</TooltipContent>
            </Tooltip>
        );
    }

    const name = [person.first_name, person.last_name].filter(Boolean).join(' ').trim();
    const initials = `${person.first_name?.charAt(0) ?? ''}${person.last_name?.charAt(0) ?? ''}`.toUpperCase() || '?';

    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <Link href={`/employees/${person.id}`} aria-label={`${name} — ${role}`}>
                    <Avatar className="hover:ring-ring size-7 shrink-0 hover:ring-2">
                        <AvatarImage src={person.avatar || ''} alt={name} />
                        <AvatarFallback className="text-[10px]">{initials}</AvatarFallback>
                    </Avatar>
                </Link>
            </TooltipTrigger>
            <TooltipContent>
                <div className="font-medium">{name}</div>
                <div className="opacity-80">{role}</div>
            </TooltipContent>
        </Tooltip>
    );
}
